import { useMemo } from 'react';
import { Quote } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { formatCalls, resolveRampClass } from './nodeChartTheme';

/**
 * Чек-лист узла: пункт → средняя оценка, доля выполнения и отдельная колонка «Н/П».
 * «Н/П» приходит из бэкенда как отдельный счётчик, потому что в среднюю оценку
 * такие пункты намеренно не входят.
 *
 * Оценка приходит в 0..1, а рампа темы работает в процентах — нормализуем здесь.
 * Прогнать 0.67 через рампу напрямую значило бы покрасить хорошую оценку в красный.
 */
const NodeChecklistTable = ({ chart, rows, onSelect, onShowExamples }) => {
  const naLabel = useMemo(
    () => chart.scoreOptions?.find((option) => option.score === null)?.label || 'Н/П',
    [chart.scoreOptions]
  );

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {/* Названия пунктов приходят из пакета и бывают длинными — отдаём им всю
                свободную ширину, числовые колонки держим фиксированными. */}
            <TableHead className="w-full max-sm:min-w-[9rem]">Пункт</TableHead>
            <TableHead className="whitespace-nowrap text-right">
              {/* На телефоне короткая подпись: полная не даёт колонке «Пункт» и сотни пикселей. */}
              <span className="sm:hidden" title="Средняя оценка">Оценка</span>
              <span className="max-sm:hidden">Средняя оценка</span>
            </TableHead>
            <TableHead className="whitespace-nowrap text-right">Выполнено</TableHead>
            <TableHead className="whitespace-nowrap text-right max-md:hidden">{naLabel}</TableHead>
            <TableHead className="whitespace-nowrap text-right max-md:hidden">Звонков</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => (
            <TableRow
              key={item.code}
              className="cursor-pointer"
              data-ev="nodes.checklist.open_journal"
              onClick={() => onSelect?.(item)}
              title="Открыть журнал звонков с проваленным пунктом"
            >
              <TableCell className="align-top">{item.label}</TableCell>
              <TableCell className="whitespace-nowrap text-right align-top">
                <span className={`rounded px-2 py-0.5 ${resolveRampClass(item.avgScore === null ? null : item.avgScore * 100)}`}>
                  {item.avgScore === null ? '—' : item.avgScore.toFixed(2)}
                </span>
              </TableCell>
              <TableCell className="whitespace-nowrap text-right align-top">
                {item.passRate === null ? '—' : `${item.passRate.toFixed(1)}%`}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right align-top text-muted-foreground max-md:hidden">
                {formatCalls(item.naCalls)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right align-top max-md:hidden">{formatCalls(item.calls)}</TableCell>
              <TableCell className="text-right align-top">
                {/* Клик по строке ведёт в журнал; отдельная кнопка — цитаты, которыми нода обосновала провал. */}
                <button
                  type="button"
                  data-ev="nodes.checklist.show_examples"
                  title="Примеры провалов с цитатами"
                  className="rounded p-1 text-muted-foreground hover:bg-muted"
                  onClick={(event) => {
                    event.stopPropagation();
                    onShowExamples?.({
                      itemKey: item.code,
                      maxScore: 0.5,
                      title: `${item.label} · провалы`
                    });
                  }}
                >
                  <Quote className="h-4 w-4" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default NodeChecklistTable;
